import { ASSET_BY_ID, isAssetUsable, type AssetEntry } from "@nano/content";
import { BUILD } from "../../env.ts";

/**
 * Resolves art by manifest id. Production only uses approved art; dev and
 * preview builds also show art pending review. Missing art in dev falls
 * back to clearly-labelled test shapes (see testArt.ts); production never
 * ships those — `pnpm check:release` fails first.
 */
export function assetEntry(id: string): AssetEntry | undefined {
  const entry = ASSET_BY_ID.get(id);
  return isAssetUsable(entry, BUILD.allowPendingArt) ? entry : undefined;
}

export function assetUrl(id: string): string | undefined {
  return assetEntry(id)?.file;
}

const fallbackCache = new Map<string, string>();

/** Image URL for DOM use; dev builds get a generated stand-in when art is missing. */
export function imageUrl(id: string): string | undefined {
  const url = assetUrl(id);
  if (url) return url;
  if (!BUILD.allowTestArt) return undefined;
  let cached = fallbackCache.get(id);
  if (!cached && typeof document !== "undefined") {
    cached = drawPlaceholder(id).toDataURL();
    fallbackCache.set(id, cached);
  }
  return cached;
}

/** Dev-only "asset-pending" marker: a dashed tile with the id written on it. */
export function drawPlaceholder(id: string, size = 128): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  ctx.fillStyle = `hsl(${hash % 360} 70% 80%)`;
  ctx.fillRect(4, 4, size - 8, size - 8);
  ctx.setLineDash([8, 6]);
  ctx.strokeStyle = "#7a4dd8";
  ctx.lineWidth = 4;
  ctx.strokeRect(4, 4, size - 8, size - 8);
  ctx.fillStyle = "#3b2a5c";
  ctx.font = "bold 14px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("asset-pending", size / 2, size / 2 - 8);
  ctx.font = "11px sans-serif";
  ctx.fillText(id.slice(0, 18), size / 2, size / 2 + 12);
  return canvas;
}
